import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Flex, Form, Input, Table, Tooltip, Typography, message } from 'antd';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { PrintButton } from '../../components/print/PrintButton';
import { invalidateAfterRefund, type Refund } from '../../components/refund-api';
import { ApiError } from '../../lib/api-client';
import { formatMoney } from '../../lib/money';
import { listRefundsToPay, payRefund } from './api';

const { Text, Title } = Typography;

/**
 * Refunds to pay: what a manager approved and the Till now hands back. The
 * approver never pays their own approval, so the button is disabled for them.
 *
 * As at the rest of the till, the idempotency key is generated ONCE when the
 * drawer opens, never per click. A retry of the same payout reuses it; a retry
 * with a different reference is a different payout and gets a fresh key.
 * The card is hidden when the list loaded empty and when the role cannot see it (403); a failed load shows an alert.
 */
export function RefundsToPay({ drawerOpen }: { drawerOpen: boolean }): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [paying, setPaying] = useState<Refund | null>(null);
  const [reference, setReference] = useState('');
  const [payKey, setPayKey] = useState('');
  const [keySentWith, setKeySentWith] = useState<string | null>(null);
  // Set once the refund is paid: the drawer stays open as a done state.
  const [paid, setPaid] = useState<Refund | null>(null);

  const list = useInfiniteQuery({
    queryKey: ['refunds-to-pay'],
    queryFn: ({ pageParam }) => listRefundsToPay(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore && last.nextCursor !== null ? last.nextCursor : undefined),
    retry: false,
  });
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];

  const openUid = useRef<string | null>(null);
  useEffect(() => {
    openUid.current = paying?.uid ?? null;
  }, [paying]);

  const pay = useMutation({
    mutationFn: ({ target, key }: { target: Refund; key: string }) =>
      payRefund(
        target.uid,
        reference.trim().length > 0 ? { reference: reference.trim() } : {},
        key,
      ),
    onSuccess: (result, { target }) => {
      const source = target.source;
      for (const queryKey of [
        ['refunds-to-pay'],
        ['till-session'],
        ['session'],
        ['customer-credit'],
        ['invoices'],
        ['bookings'],
        [source.type === 'INVOICE' ? 'invoice' : 'booking', source.uid],
        ['booking-money', source.uid],
      ]) {
        void queryClient.invalidateQueries({ queryKey });
      }
      invalidateAfterRefund(queryClient);
      // The drawer turns into a done state; the key stays until Done.
      if (openUid.current === target.uid) setPaid(result);
    },
    onError: (e: unknown) => {
      // "Already paid" and the like mean the list is out of date.
      void queryClient.invalidateQueries({ queryKey: ['refunds-to-pay'] });
      void message.error(e instanceof ApiError ? e.message : t('refunds.payFailed'));
    },
  });

  const closePaying = (): void => {
    setPaying(null);
    setPaid(null);
  };

  const start = (row: Refund): void => {
    setPaid(null);
    setReference('');
    setPayKey(crypto.randomUUID());
    setKeySentWith(null);
    setPaying(row);
  };

  const needsReference = paying !== null && paying.method !== 'CASH';
  const referenceMissing = needsReference && reference.trim().length === 0;
  const noDrawer = paying?.method === 'CASH' && !drawerOpen;

  const submit = (): void => {
    const body = reference.trim();
    let key = payKey;
    if (keySentWith !== null && keySentWith !== body) {
      key = crypto.randomUUID();
      setPayKey(key);
    }
    setKeySentWith(body);
    if (paying !== null) pay.mutate({ target: paying, key });
  };

  // Hidden only when the role cannot see it, or when the list loaded and is empty.
  // Any other failure shows the alert: a card that vanishes hides unpaid refunds.
  const hidden =
    (list.error instanceof ApiError && list.error.status === 403) || (list.isSuccess && rows.length === 0);
  if (hidden && paying === null) return null;

  const methodLabel = (m: string): string => t(`refunds.method.${m}`, { defaultValue: m });

  return (
    <>
      <Card title={t('refunds.toPay.title')} styles={{ body: { padding: 0 } }}>
        {list.error !== null && !(list.error instanceof ApiError && list.error.status === 403) && (
          <Alert
            type="error"
            showIcon
            banner
            message={list.error instanceof ApiError ? list.error.message : t('refunds.toPay.loadFailed')}
          />
        )}
        <Table<Refund>
          dataSource={rows}
          rowKey={(row) => row.uid}
          loading={list.isFetching}
          pagination={false}
          size="middle"
          columns={[
            {
              title: t('refunds.toPay.customer'),
              key: 'customer',
              render: (_: unknown, row: Refund) => (
                <Flex vertical gap={2}>
                  <Text strong>{row.customerName ?? t('till.walkIn')}</Text>
                  <Text type="secondary" className="tabular">
                    {row.source.no}
                  </Text>
                </Flex>
              ),
            },
            {
              title: t('refunds.toPay.amount'),
              key: 'amount',
              render: (_: unknown, row: Refund) => (
                <Flex vertical gap={2}>
                  <Text strong className="tabular" style={{ whiteSpace: 'nowrap' }}>
                    {formatMoney(row.amount)}
                  </Text>
                  <Text type="secondary">{methodLabel(row.method)}</Text>
                </Flex>
              ),
            },
            {
              title: t('refunds.toPay.approvedBy'),
              key: 'approvedBy',
              // The amount and the Pay button matter at 390px; the approver shows in the drawer.
              responsive: ['md' as const],
              render: (_: unknown, row: Refund) => <Text>{row.approvedByName}</Text>,
            },
            {
              title: '',
              key: 'pay',
              align: 'right' as const,
              render: (_: unknown, row: Refund) => (
                <Tooltip title={row.canPay ? undefined : t('refunds.notYours')}>
                  <span>
                    <Button
                      type="primary"
                      size="large"
                      disabled={!row.canPay}
                      onClick={() => {
                        start(row);
                      }}
                    >
                      {t('refunds.pay')}
                    </Button>
                  </span>
                </Tooltip>
              ),
            },
          ]}
        />
        {list.hasNextPage && (
          <Flex justify="center" style={{ padding: 12 }}>
            <Button
              size="large"
              loading={list.isFetchingNextPage}
              onClick={() => {
                void list.fetchNextPage();
              }}
            >
              {t('till.bookings.more')}
            </Button>
          </Flex>
        )}
      </Card>

      <ActionDrawer
        open={paying !== null}
        title={t('refunds.payTitle', { name: paying?.customerName ?? t('till.walkIn') })}
        submitLabel={t('refunds.payAmount', { amount: formatMoney(paying?.amount ?? '0') })}
        submitting={pay.isPending}
        submitDisabled={referenceMissing || noDrawer}
        onClose={closePaying}
        onSubmit={submit}
        footer={
          paid === null ? undefined : (
            <Flex justify="flex-end">
              <Button type="primary" size="large" onClick={closePaying}>
                {t('till.done')}
              </Button>
            </Flex>
          )
        }
        consequence={
          paying === null || paid !== null
            ? undefined
            : t('refunds.payConsequence', {
                amount: paying.amount,
                method: methodLabel(paying.method),
                where: t(`refunds.where.${paying.method}`, { defaultValue: methodLabel(paying.method) }),
              })
        }
      >
        {paid !== null ? (
          <Flex vertical gap={16}>
            <Alert type="success" showIcon message={
                paid.refundNo === null
                  ? t('refunds.doneTitleNoNumber')
                  : t('refunds.doneTitle', { refundNo: paid.refundNo })
              } />
            <PrintButton doc="refund" uid={paid.uid} label={t('print.printRefund')} type="primary" />
          </Flex>
        ) : null}
        {paying !== null && paid === null && (
          <Flex vertical gap={16}>
            <Title level={2} style={{ margin: 0 }} className="tabular">
              {formatMoney(paying.amount)}
            </Title>
            <Flex vertical gap={4}>
              <Text strong>{paying.customerName ?? t('till.walkIn')}</Text>
              <Text type="secondary" className="tabular">
                {paying.source.no}
              </Text>
              <Text>{methodLabel(paying.method)}</Text>
              <Text type="secondary">{t('refunds.approvedByName', { name: paying.approvedByName })}</Text>
            </Flex>
            {noDrawer && (
              <Alert type="error" showIcon message={t('till.noDrawer')} description={t('till.noDrawerHelp')} />
            )}
            {paying.method !== 'CASH' && (
              <Form layout="vertical">
                <Form.Item label={t('till.fields.reference')} htmlFor="refund-pay-reference" required help={t('till.referenceHelp')}>
                  <Input
                    id="refund-pay-reference"
                    size="large"
                    value={reference}
                    onChange={(e) => {
                      setReference(e.target.value);
                    }}
                  />
                </Form.Item>
              </Form>
            )}
          </Flex>
        )}
      </ActionDrawer>
    </>
  );
}
